import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Cpu, Users, Link2, UserPlus, CheckCircle2, Sparkles } from "lucide-react";

const BUILTIN_MODELS = [
  "nvidia/nemotron-3-super-120b-a12b",
  "nvidia/llama-3.1-nemotron-70b-instruct",
  "meta/llama-3.1-70b-instruct",
];

const APPROVAL = [
  { v: "suggest", t: "Suggest only", d: "AI only suggests, never acts." },
  { v: "draft", t: "Draft & approve", d: "AI drafts, you approve before execution." },
  { v: "auto", t: "Auto-execute safe", d: "Safe workflows run automatically." },
];

export default function Settings() {
  const { user } = useAuth();
  const [ai, setAi] = useState(null);
  const [integrations, setIntegrations] = useState({ google: {}, telegram: {} });
  const [members, setMembers] = useState([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("member");

  useEffect(() => {
    api.get("/workspace").then((r) => setAi(r.data.ai_settings));
    api.get("/integrations").then((r) => setIntegrations(r.data));
    api.get("/workspace/members").then((r) => setMembers(r.data));
    const params = new URLSearchParams(window.location.search);
    if (params.get("google") === "connected") toast.success("Google Workspace connected");
    if (params.get("google") === "error") toast.error("Google connection failed");
  }, []);

  const saveAi = async (patch) => {
    const next = { ...ai, ...patch };
    setAi(next);
    const { data } = await api.put("/workspace/ai-settings", patch);
    setAi(data);
  };
  const saveAndToast = async () => { await api.put("/workspace/ai-settings", ai); toast.success("AI settings saved"); };

  const connectGoogle = async () => {
    try {
      const { data } = await api.get("/oauth/google/login");
      window.location.href = data.authorization_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Google is not configured yet");
    }
  };
  const disconnect = async (t) => {
    await api.post(`/integrations/${t}/disconnect`, {});
    api.get("/integrations").then((r) => setIntegrations(r.data));
  };

  const reloadMembers = () => api.get("/workspace/members").then((r) => setMembers(r.data));

  const invite = async () => {
    if (!inviteEmail.trim()) return;
    try {
      await api.post("/workspace/invite", { email: inviteEmail, role: inviteRole });
      toast.success("Member added");
      setInviteEmail("");
      reloadMembers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invite failed");
    }
  };

  const changeRole = async (memberId, role) => {
    try {
      await api.put(`/workspace/members/${memberId}/role`, { role });
      toast.success("Role updated");
      reloadMembers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't update role");
    }
  };

  const removeMember = async (memberId) => {
    try {
      await api.delete(`/workspace/members/${memberId}`);
      toast.success("Member removed");
      reloadMembers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't remove member");
    }
  };

  const myRole = members.find((m) => m.id === user?.id)?.role;
  const canManage = myRole === "owner" || myRole === "admin";

  if (!ai) return <div className="label-mono text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <div className="label-mono text-slate-500">Configuration</div>
        <h1 className="text-4xl font-black tracking-tighter">Settings</h1>
      </div>

      <Tabs defaultValue="ai">
        <TabsList className="rounded-sm">
          <TabsTrigger value="ai" className="rounded-sm" data-testid="tab-ai"><Cpu size={14} className="mr-1.5" /> AI Engine</TabsTrigger>
          <TabsTrigger value="integrations" className="rounded-sm" data-testid="tab-integrations"><Link2 size={14} className="mr-1.5" /> Integrations</TabsTrigger>
          <TabsTrigger value="team" className="rounded-sm" data-testid="tab-team"><Users size={14} className="mr-1.5" /> Team</TabsTrigger>
        </TabsList>

        <TabsContent value="ai">
          <Card className="rounded-sm border-border shadow-none p-6 space-y-6 max-w-2xl">
            <div>
              <h3 className="text-xl font-bold tracking-tight">AI Provider</h3>
              <p className="text-sm text-slate-600">Choose the engine powering your copilot. Default uses the server's NVIDIA NIM key.</p>
            </div>

            <div className="flex gap-2">
              {["builtin", "custom"].map((m) => (
                <button key={m} onClick={() => saveAi({ mode: m })}
                  className={`flex-1 border rounded-sm p-3 text-left transition-colors ${ai.mode === m ? "border-ai bg-ai/5" : "border-border hover:border-slate-400"}`}
                  data-testid={`ai-mode-${m}`}>
                  <div className="font-semibold text-sm capitalize">{m === "builtin" ? "Built-in (NVIDIA)" : "Custom / Self-hosted"}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{m === "builtin" ? "Server NVIDIA NIM key" : "OpenRouter, Ollama, LM Studio, Grok, Azure…"}</div>
                </button>
              ))}
            </div>

            {ai.mode !== "custom" ? (
              <div>
                <label className="label-mono text-slate-500">Model</label>
                <Select value={ai.builtin_model} onValueChange={(v) => saveAi({ builtin_model: v })}>
                  <SelectTrigger className="rounded-sm" data-testid="ai-model-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {BUILTIN_MODELS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <label className="label-mono text-slate-500">Base URL (OpenAI-compatible)</label>
                  <Input value={ai.custom_base_url || ""} onChange={(e) => setAi({ ...ai, custom_base_url: e.target.value })}
                    placeholder="https://openrouter.ai/api/v1 or http://localhost:11434/v1" className="rounded-sm" data-testid="ai-base-url-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="label-mono text-slate-500">Model</label>
                    <Input value={ai.custom_model || ""} onChange={(e) => setAi({ ...ai, custom_model: e.target.value })} placeholder="llama3 / grok-2 / …" className="rounded-sm" data-testid="ai-custom-model-input" />
                  </div>
                  <div>
                    <label className="label-mono text-slate-500">API Key (optional)</label>
                    <Input type="password" value={ai.custom_api_key || ""} onChange={(e) => setAi({ ...ai, custom_api_key: e.target.value })} placeholder="sk-…" className="rounded-sm" />
                  </div>
                </div>
              </div>
            )}

            <div>
              <label className="label-mono text-slate-500 flex items-center gap-1.5 mb-2"><Sparkles size={12} className="text-ai" /> Agent approval mode</label>
              <div className="grid md:grid-cols-3 gap-2">
                {APPROVAL.map((a) => (
                  <button key={a.v} onClick={() => saveAi({ approval_mode: a.v })}
                    className={`border rounded-sm p-3 text-left transition-colors ${ai.approval_mode === a.v ? "border-ai bg-ai/5" : "border-border hover:border-slate-400"}`}
                    data-testid={`approval-${a.v}`}>
                    <div className="font-semibold text-sm">{a.t}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{a.d}</div>
                  </button>
                ))}
              </div>
            </div>

            <Button onClick={saveAndToast} className="rounded-sm bg-slate-950 hover:bg-slate-800" data-testid="save-ai-settings">Save AI Settings</Button>
          </Card>
        </TabsContent>

        <TabsContent value="integrations">
          <div className="grid md:grid-cols-2 gap-5 max-w-3xl">
            <Card className="rounded-sm border-border shadow-none p-6">
              <div className="flex items-center justify-between">
                <div className="font-bold text-lg">Google Workspace</div>
                {integrations.google?.status === "connected" && <CheckCircle2 size={18} className="text-green-600" />}
              </div>
              <p className="text-sm text-slate-600 mt-1">Calendar two-way sync + Gmail summaries & drafts.</p>
              {integrations.google?.status === "connected" ? (
                <Button variant="outline" className="rounded-sm mt-4" onClick={() => disconnect("google")} data-testid="google-disconnect">Disconnect</Button>
              ) : (
                <Button className="rounded-sm bg-slate-950 hover:bg-slate-800 mt-4" onClick={connectGoogle} data-testid="google-connect"><UserPlus size={16} className="mr-2" /> Connect</Button>
              )}
              <p className="text-xs text-muted-foreground mt-3">Requires Google OAuth credentials for live sync.</p>
            </Card>

            <Card className="rounded-sm border-border shadow-none p-6">
              <div className="flex items-center justify-between">
                <div className="font-bold text-lg">Telegram</div>
                {integrations.telegram?.status === "connected" && <CheckCircle2 size={18} className="text-green-600" />}
              </div>
              <p className="text-sm text-slate-600 mt-1">Bot assistant, notifications & message → task.</p>
              <a href="/telegram"><Button variant="outline" className="rounded-sm mt-4" data-testid="telegram-setup">Configure in Telegram tab</Button></a>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="team">
          <Card className="rounded-sm border-border shadow-none p-6 max-w-2xl">
            <h3 className="text-xl font-bold tracking-tight mb-1">Team Members</h3>
            <p className="text-sm text-slate-600 mb-1">Invite teammates by email (they must have an account).</p>
            <p className="text-xs text-slate-500 mb-4">
              Roles: <b>viewer</b> (read-only) · <b>member</b> (edit content) · <b>admin</b> (+ settings, integrations, members) · <b>owner</b> (full).
              {!canManage && <> You are a <b>{myRole}</b> — only owners/admins manage the team.</>}
            </p>
            {canManage && (
              <div className="flex gap-2 mb-5">
                <Input value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="teammate@company.com" className="rounded-sm" data-testid="invite-email-input" />
                <Select value={inviteRole} onValueChange={setInviteRole}>
                  <SelectTrigger className="rounded-sm w-32" data-testid="invite-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="viewer">Viewer</SelectItem>
                    <SelectItem value="member">Member</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
                <Button onClick={invite} className="rounded-sm bg-slate-950 hover:bg-slate-800" data-testid="invite-button"><UserPlus size={16} className="mr-2" /> Invite</Button>
              </div>
            )}
            <div className="space-y-2">
              {members.map((m) => {
                const isOwner = m.role === "owner";
                const isSelf = m.id === user?.id;
                return (
                <div key={m.id} className="flex items-center gap-3 py-2.5 border-b border-border last:border-0">
                  <div className="w-9 h-9 bg-slate-950 text-white rounded-sm flex items-center justify-center text-xs font-bold">
                    {(m.name || "U").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-medium">{m.name} {isSelf && <span className="text-slate-400">(you)</span>}</div>
                    <div className="text-xs text-slate-500">{m.email}</div>
                  </div>
                  {canManage && !isOwner && !isSelf ? (
                    <>
                      <Select value={m.role} onValueChange={(v) => changeRole(m.id, v)}>
                        <SelectTrigger className="rounded-sm w-28 h-8" data-testid={`role-select-${m.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="viewer">viewer</SelectItem>
                          <SelectItem value="member">member</SelectItem>
                          <SelectItem value="admin">admin</SelectItem>
                        </SelectContent>
                      </Select>
                      <button onClick={() => removeMember(m.id)} data-testid={`remove-${m.id}`} className="text-muted-foreground hover:text-destructive text-xs font-semibold">Remove</button>
                    </>
                  ) : (
                    <span className="label-mono text-slate-500">{m.role}</span>
                  )}
                </div>
              );})}
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
